import { captureException } from '@sentry/browser';
import {
  QUIZ_LIMITS_REACHED_ERROR,
  QuizImportMethods,
} from '@showbie-socrative/socrative-utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { translate } from '~l10n/client';

import { toast } from '~shared/components/Toast';
import {
  QuizToastActions,
  apiToaster,
} from '~teacher/components/quizzes/toastActions';
import { quizzesQueryKey } from '~teacher/hook/useQuizzes';
import { addQuizToCache } from '~teacher/hook/useQuizzes';

import { isAxiosError } from '../../api/isAxiosError';
import { uniqueIdFactory } from '../../api/quiz/getQuizzes/normalize/uniqueIds';
import {
  ImportQuizBySocParams,
  ResponseBody,
  importQuizToPersonalBySoc,
} from '../../api/quiz/importQuiz';

type ImportQuizBySocCodeParams = Pick<
  ImportQuizBySocParams,
  'socNumber' | 'parentId'
>;

export function useImportQuizToPersonalBySocMutation(
  quizImportMethod: QuizImportMethods
) {
  const queryClient = useQueryClient();
  return useMutation(
    // Actual mutation function (first argument to useMutation) -
    // fires after onMutate and needed for isLoading, etc.
    ({ parentId, socNumber }: ImportQuizBySocCodeParams) => {
      return importQuizToPersonalBySoc({
        parentId: parentId,
        socNumber: socNumber,
        importMethod: quizImportMethod,
      });
    },

    // Mutation options (second argument to useMutation) - contains
    // onMutate, onError, onSettled, etc.
    {
      onMutate: async () => {
        await queryClient.cancelQueries(quizzesQueryKey.quiz);
      },

      onSuccess: (importQuizBySocResp: ResponseBody) => {
        const personalCache = queryClient.getQueryData(quizzesQueryKey.quiz);

        if (!personalCache) {
          // there is no cache, nothing to update
          return;
        }

        const { date, id, name, sharable, soc, version, parentId } =
          importQuizBySocResp;
        const uniqueId = uniqueIdFactory.quiz.toUniqueId(id);
        addQuizToCache(queryClient, {
          id: id,
          name: name,
          parentId: parentId,
          type: 'quiz',
          uniqueId: uniqueId,
          date: new Date(date),
          soc: soc,
          deleted: false,
          version: version,
          sharable: sharable,
          standard: null,
        });
      },
      // React-query recommends always fetching after error or success
      onSettled: () => {
        queryClient.invalidateQueries(quizzesQueryKey.quiz);
      },
    }
  );
}

/**
 * To simplify duplicated code. Use this helper method to show generic toast messages when required
 * @param onQuizLimitReached - Callback to use when you want to use your own unique logic when quiz limits are reached
 */
export const handleImportQuizError =
  (onQuizLimitReached: () => void) => (error: unknown) => {
    if (!isAxiosError(error)) {
      // If this isn't an axios error, this is probably worth logging.
      captureException(error);
      apiToaster([], QuizToastActions.IMPORT, error);
    } else {
      if (error.response?.status >= 500) {
        // 500 errors are server errors... we want to capture those
        captureException(error);
        apiToaster([], QuizToastActions.IMPORT, error);
      } else if (error?.response?.status === 429) {
        toast.critical(
          translate(
            'Too many requests. Please wait a while and try again later.' // TODO REPLACE ME
          )
        );
      } else if (
        error?.response?.data?.code === QUIZ_LIMITS_REACHED_ERROR.code
      ) {
        onQuizLimitReached();
      } else {
        apiToaster([], QuizToastActions.IMPORT, error);
      }
    }
  };
